import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/client";
import TopBar from "../components/layout/TopBar";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  addMonths, subMonths, addWeeks, subWeeks, isSameMonth, isSameDay, isToday,
  isBefore, startOfDay
} from "date-fns";

const STATUS_DOT = {
  New: "bg-blue-400", Open: "bg-sky-400", "In Progress": "bg-amber-400",
  Waiting: "bg-orange-400", Completed: "bg-green-400", Archived: "bg-gray-400",
};

const PRIORITY_BORDER = {
  Low: "border-l-gray-300", Normal: "border-l-blue-400", High: "border-l-orange-400", Urgent: "border-l-red-500",
};

// Pastel card colors based on due date status
const getTaskChipBg = (task) => {
  if (task.status === "Completed" || task.status === "Archived") return "#F8F9FA";
  if (task.due_date) {
    const today = startOfDay(new Date());
    const due = new Date(task.due_date + "T00:00:00");
    if (isBefore(due, today)) return "#FFACAB";
    if (isToday(due)) return "#FFD7B3";
  }
  if (task.status === "New") return "#F8F9FA";
  if (["Open", "In Progress", "Waiting"].includes(task.status)) return "#E9FFF3";
  return "#F8F9FA";
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({ status: "", priority: "", owner_id: "", my_tasks: false });
  const [users, setUsers] = useState([]);

  const fetchTasks = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.priority) params.set("priority", filters.priority);
    if (filters.owner_id) params.set("owner_id", filters.owner_id);
    if (filters.my_tasks) params.set("my_tasks", "true");
    const { data } = await API.get(`/tasks?${params}`);
    setTasks(data.filter(t => t.due_date));
  }, [filters]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => { API.get("/users").then(r => setUsers(r.data)).catch(() => {}); }, []);

  const navigate_period = (dir) => {
    if (view === "month") setCurrentDate(d => dir > 0 ? addMonths(d, 1) : subMonths(d, 1));
    else setCurrentDate(d => dir > 0 ? addWeeks(d, 1) : subWeeks(d, 1));
  };

  const getDays = () => {
    if (view === "month") {
      const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    }
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  };

  const getTasksForDay = (day) => {
    const dateStr = format(day, "yyyy-MM-dd");
    return tasks.filter(t => t.due_date === dateStr);
  };

  const days = getDays();
  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <>
      <TopBar title="Calendar" onCreateTask={() => navigate("/tasks?create=true")} />
      <div className="flex-1 overflow-y-auto p-6" data-testid="calendar-page">
        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate_period(-1)} data-testid="cal-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h2 className="text-base font-semibold font-['Manrope'] min-w-[180px] text-center">
              {view === "month" ? format(currentDate, "MMMM yyyy") : `Week of ${format(days[0], "MMM d")} - ${format(days[6], "MMM d, yyyy")}`}
            </h2>
            <Button variant="outline" size="sm" onClick={() => navigate_period(1)} data-testid="cal-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} className="text-xs">
              Today
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={view === "month" ? "default" : "outline"} size="sm" className="text-xs"
              onClick={() => setView("month")} data-testid="cal-month-view"
            >Month</Button>
            <Button
              variant={view === "week" ? "default" : "outline"} size="sm" className="text-xs"
              onClick={() => setView("week")} data-testid="cal-week-view"
            >Week</Button>
            <Select value={filters.status || "all"} onValueChange={v => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {["New", "Open", "In Progress", "Waiting", "Completed"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.priority || "all"} onValueChange={v => setFilters(f => ({ ...f, priority: v === "all" ? "" : v }))}>
              <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                {["Low", "Normal", "High", "Urgent"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant={filters.my_tasks ? "default" : "outline"} size="sm" className="h-8 text-xs"
              onClick={() => setFilters(f => ({ ...f, my_tasks: !f.my_tasks }))} data-testid="cal-my-tasks"
            >My Tasks</Button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white border border-[#EBEBEB] rounded-md overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-7 border-b border-[#EBEBEB]">
            {weekDays.map(d => (
              <div key={d} className="px-2 py-2 text-xs font-medium text-[#686B74] text-center">{d}</div>
            ))}
          </div>
          {/* Days */}
          <div className="grid grid-cols-7">
            {days.map((day, i) => {
              const dayTasks = getTasksForDay(day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              return (
                <div
                  key={i}
                  className={`min-h-[100px] border-b border-r border-[#EBEBEB] p-1.5 ${
                    !isCurrentMonth ? "bg-[#F9F9FB]" : ""
                  } ${view === "week" ? "min-h-[200px]" : ""}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                        isToday(day) ? "bg-[#111216] text-white" : isCurrentMonth ? "text-[#111216]" : "text-[#686B74]/50"
                      }`}
                    >
                      {format(day, "d")}
                    </span>
                    <button
                      className="opacity-0 hover:opacity-100 transition-opacity"
                      onClick={() => navigate(`/tasks?create=true&date=${format(day, "yyyy-MM-dd")}`)}
                    >
                      <Plus className="w-3 h-3 text-[#686B74]" />
                    </button>
                  </div>
                  <div className="space-y-0.5">
                    {dayTasks.slice(0, view === "week" ? 8 : 3).map(t => (
                      <div
                        key={t.id}
                        onClick={() => navigate(`/tasks/${t.id}`)}
                        className={`text-[11px] px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity truncate border-l-2 ${PRIORITY_BORDER[t.priority] || "border-l-gray-300"}`}
                        style={{ backgroundColor: getTaskChipBg(t) }}
                      >
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${STATUS_DOT[t.status] || "bg-gray-400"}`} />
                        {t.title}
                      </div>
                    ))}
                    {dayTasks.length > (view === "week" ? 8 : 3) && (
                      <span className="text-[10px] text-[#686B74] pl-1">+{dayTasks.length - (view === "week" ? 8 : 3)} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
